import { useEffect, type ReactNode } from "react";

interface PickerSheetProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: ReactNode;
}

/**
 * Full-screen bottom sheet for mobile weapon/equipment pickers.
 * Slides up from the bottom and covers the viewport.
 * On desktop this component should not be used — inline pickers are shown instead.
 */
export function PickerSheet({ isOpen, onClose, title, children }: PickerSheetProps) {
	// Lock body scroll when open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	// Close on Escape
	useEffect(() => {
		if (!isOpen) return;
		function handleKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [isOpen, onClose]);

	return (
		<div
			className={`fixed inset-0 z-50 flex flex-col transition-opacity duration-200 ${
				isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
			}`}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/60" onClick={onClose} />

			{/* Sheet */}
			<div
				className={`relative mt-auto w-full max-h-[85vh] bg-bg-secondary rounded-t-xl border-t border-border-default shadow-lg flex flex-col transform transition-transform duration-300 ease-out ${
					isOpen ? "translate-y-0" : "translate-y-full"
				}`}
			>
				{/* Handle + Header */}
				<div className="shrink-0 pt-2 pb-3 px-4 border-b border-border-default">
					{/* Drag handle indicator */}
					<div className="w-10 h-1 bg-border-default rounded-full mx-auto mb-3" />
					<div className="flex items-center justify-between">
						<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
						<button
							type="button"
							onClick={onClose}
							className="text-text-muted hover:text-text-primary transition-colors p-1 min-w-[44px] min-h-[44px] flex items-center justify-center cursor-pointer"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
						</button>
					</div>
				</div>

				{/* Scrollable content */}
				<div className="flex-1 overflow-y-auto p-4">
					{children}
				</div>
			</div>
		</div>
	);
}
